/**
 * M14 Help content: static, in-repo (intake A1). No fetch, so no <Async>.
 *
 * Each article states only behaviour that ships today; the owner reviews the
 * copy. ORDER IS ANALYTICS-SIGNIFICANT: `help_article_opened` sends the index
 * into this array (`item_index`), and support.test.ts pins the slug order.
 * Append; never reorder or insert.
 */
export type Article = {
  slug: string
  title: string
  summary: string
  body: readonly string[]
  keywords: readonly string[]
}

export const ARTICLES: readonly Article[] = [
  {
    slug: 'connect-a-bank',
    title: 'Connect a bank',
    summary: 'Link an account so TrackE5 can bring in balances and transactions.',
    body: [
      'Open Accounts and choose Connect account (Add account when you have none yet), then Link a bank. You can also choose Add manually to track an account that has no bank link.',
      'Bank links are read-only. TrackE5 can see balances and transactions, and can never initiate a payment, move money, or change your bank connections.',
      'Depending on your plan, adding another account may need Premium. If it does, you will see an Upgrade action.',
    ],
    keywords: ['link', 'account', 'accounts', 'connection', 'manual', 'read-only', 'premium'],
  },
  {
    slug: 'out-of-date-data',
    title: 'Why is my data out of date?',
    summary: 'What the "Showing data from" note means.',
    body: [
      'When TrackE5 cannot reach your bank for a fresh update, it keeps showing the last data it has and tells you the date it is from, in a note that reads "Showing data from" that date.',
      'That is real data, not an error: it is simply the most recent data TrackE5 has.',
      'If an account says it needs reconnecting, open Connections to see which bank link is affected.',
    ],
    keywords: ['stale', 'sync', 'refresh', 'old', 'reconnect', 'connections', 'date'],
  },
  {
    slug: 'categories-and-rules',
    title: 'Categories and auto-rules',
    summary: 'Organise spending, and let rules categorise it for you.',
    body: [
      'Categories groups your spending. Use New category to add your own.',
      'Auto-rules assign a category to matching transactions automatically. Open a rule to see what it has been applied to and what it matches now, and use Apply now to run it over existing transactions.',
      'The Suggestions tab on Auto-rules proposes rules for you to review.',
    ],
    keywords: ['category', 'rule', 'rules', 'auto-rules', 'suggestions', 'categorise', 'apply'],
  },
  {
    slug: 'household-members',
    title: 'Household members',
    summary: 'Share your finances with other people, with a role for each.',
    body: [
      'Members shows everyone in your household with their role: Owner, Admin, Member or Viewer.',
      'Owners and admins can invite people and remove members. Only an owner can change someone\'s role. You can leave the household with Leave household, unless you are its last owner, in which case make someone else an owner first.',
      'Invitations you have sent that have not been accepted yet appear under Pending invitations.',
    ],
    keywords: ['household', 'invite', 'invitation', 'members', 'role', 'owner', 'admin', 'viewer', 'leave', 'share'],
  },
  {
    slug: 'two-step-verification',
    title: 'Two-step verification',
    summary: 'Ask for a second code at sign-in.',
    body: [
      'Open Settings, then Security. You can add an authenticator app, a phone number, or both.',
      'You can have one authenticator app at a time. To use a different one, remove the current one first.',
      'Removing your last verification method asks you to confirm your identity again.',
    ],
    keywords: ['2fa', 'mfa', 'security', 'authenticator', 'phone', 'sign in', 'password', 'code'],
  },
  {
    slug: 'plan-and-billing',
    title: 'Plan and billing',
    summary: 'Where Premium is managed.',
    body: [
      'Some features are part of Premium. Where one is locked, you will see an Upgrade action.',
      'Plan and billing is not available in the web app yet.',
    ],
    keywords: ['premium', 'upgrade', 'subscription', 'billing', 'plan', 'pay', 'ios'],
  },
]
